import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import AutomationBuilder from '../components/AutomationBuilder';
import SEOHead from '../components/SEOHead';
import SkeletonTable from '../components/SkeletonTable';
import { Zap, GitBranch, Sparkles, ArrowRight, Webhook, Shield, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
const FEATURES = [
  { icon: Zap, title: '10 Workflow Templates', desc: 'Pre-built templates for common automation patterns', color: 'amber' },
  { icon: GitBranch, title: 'Visual Canvas', desc: 'Drag-and-drop builder with grid snap, zoom, and mini-map', color: 'sky' },
  { icon: Sparkles, title: 'AI-Powered Nodes', desc: 'AI text generation, classification, and summarization', color: 'fuchsia' },
  { icon: RefreshCw, title: 'Undo / Redo', desc: 'Full history with Ctrl+Z support and copy/paste nodes', color: 'emerald' },
];

const NODE_TYPES_SUMMARY = [
  { label: 'Triggers', count: 6, color: 'amber', items: ['Webhook', 'Schedule', 'Email', 'Form', 'Message', 'Agent Idle'] },
  { label: 'Actions', count: 9, color: 'emerald', items: ['Send Email', 'Send Slack', 'HTTP Request', 'Database Query', 'Update CRM', 'Assign Human', 'Wait/Delay', 'Webhook Call', 'Send Message'] },
  { label: 'Logic', count: 4, color: 'violet', items: ['Condition', 'Delay', 'Loop', 'Switch'] },
  { label: 'AI', count: 3, color: 'fuchsia', items: ['Text Generation', 'Classification', 'Summarization'] },
];

const INTEGRATIONS = [
  { name: 'Stripe', desc: 'Payment events', icon: '💳' },
  { name: 'Shopify', desc: 'Order & inventory sync', icon: '🛒' },
  { name: 'HubSpot', desc: 'CRM sync', icon: '📊' },
  { name: 'Salesforce', desc: 'Lead & opportunity sync', icon: '☁️' },
];

export default function AutomationsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t('automations_page.automation_builder_visual_workflow_editor_max')}
        description="Build powerful automation workflows with our visual drag-and-drop canvas. 10 templates, 22 node types, AI-powered nodes, undo/redo."
        url="/dashboard/automations"
        noIndex
      />
      <Header />

      <Breadcrumbs items={[
        { label: 'Home', path: '/' },
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Automations' },
      ]} />

      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6 md:py-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// AUTOMATIONS</div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl uppercase">
            Advanced <span className="text-amber-500">{t('automations_page.automation_builder')}</span>
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            Create powerful automation workflows with our visual flow editor. Drag nodes, connect them, and automate your business processes. Import pre-built templates or build from scratch.
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-3 sm:mt-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="font-mono text-[10px] text-zinc-500">10 Templates</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="font-mono text-[10px] text-zinc-500">22 Node Types</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-sky-500" />
              <span className="font-mono text-[10px] text-zinc-500">4 Categories</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-fuchsia-500" />
              <span className="font-mono text-[10px] text-zinc-500">{t('automations_page.ai_nodes')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {FEATURES.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <div key={i} className="border border-zinc-800 p-4 hover:border-zinc-700 transition-colors">
                  <Icon className={`w-5 h-5 mb-2 ${feat.color === 'amber' ? 'text-amber-400' : feat.color === 'sky' ? 'text-sky-400' : feat.color === 'fuchsia' ? 'text-fuchsia-400' : 'text-emerald-400'}`} />
                  <h3 className="font-mono text-[10px] uppercase tracking-wider text-white mb-1">{feat.title}</h3>
                  <p className="font-mono text-[9px] text-zinc-500">{feat.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Node Types */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">// NODE TYPES</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {NODE_TYPES_SUMMARY.map((cat, i) => (
              <div key={i} className={`border ${cat.color === 'amber' ? 'border-amber-500/20 bg-amber-500/5' : cat.color === 'emerald' ? 'border-emerald-500/20 bg-emerald-500/5' : cat.color === 'violet' ? 'border-violet-500/20 bg-violet-500/5' : 'border-fuchsia-500/20 bg-fuchsia-500/5'} p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-mono text-[10px] uppercase tracking-wider ${cat.color === 'amber' ? 'text-amber-400' : cat.color === 'emerald' ? 'text-emerald-400' : cat.color === 'violet' ? 'text-violet-400' : 'text-fuchsia-400'}`}>{cat.label}</span>
                  <span className="font-mono text-[10px] text-zinc-500">{cat.count}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {cat.items.map((item, j) => (
                    <span key={j} className={`font-mono text-[8px] px-1.5 py-0.5 border ${cat.color === 'amber' ? 'border-amber-500/20 text-amber-400/60' : cat.color === 'emerald' ? 'border-emerald-500/20 text-emerald-400/60' : cat.color === 'violet' ? 'border-violet-500/20 text-violet-400/60' : 'border-fuchsia-500/20 text-fuchsia-400/60'}`}>{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Templates */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">// INTEGRATION TEMPLATES</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {INTEGRATIONS.map((int, i) => (
              <div key={i} className="border border-zinc-800 p-3 flex items-center gap-3 hover:border-zinc-700 transition-colors">
                <span className="text-2xl">{int.icon}</span>
                <div>
                  <h4 className="font-mono text-[10px] text-white font-bold">{int.name}</h4>
                  <p className="font-mono text-[8px] text-zinc-500">{int.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Builder */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6 md:py-10">
        {loading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : (
          <AutomationBuilder />
        )}
      </div>

      {/* Keyboard shortcuts help */}
      <section className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// KEYBOARD SHORTCUTS</div>
          <div className="flex flex-wrap gap-2 sm:gap-4">
            {[
              { keys: 'Ctrl+Z', action: 'Undo' },
              { keys: 'Ctrl+Shift+Z', action: 'Redo' },
              { keys: 'Ctrl+C', action: 'Copy nodes' },
              { keys: 'Ctrl+V', action: 'Paste nodes' },
              { keys: 'Ctrl+A', action: 'Select all' },
              { keys: 'Delete', action: 'Delete selected' },
              { keys: 'Shift+Click', action: 'Multi-select' },
              { keys: 'Escape', action: 'Deselect' },
            ].map((shortcut, i) => (
              <div key={i} className="flex items-center gap-2">
                <kbd className="font-mono text-[8px] sm:text-[9px] px-1.5 py-0.5 border border-zinc-700 bg-zinc-900 text-zinc-400">{shortcut.keys}</kbd>
                <span className="font-mono text-[9px] text-zinc-600">{shortcut.action}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
